import { useState, useCallback } from 'react';
import { ProcessSupervisionChunk } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/types';
import apiFetch from 'react-scale/lib/api-fetch';

export type ProcessCritiqueHook = ReturnType<typeof useProcessCritique>;

export type ProcessCritiqueResponse = {
  step: number;
  reason: string;
  correctedStep: string;
};

export type ProcessCritiqueOutput = {
  step: null | number;
  reason: null | string;
  correctedStep: null | string;
  loading: boolean;
  hasRun: boolean;
  error: boolean;
};

export type ProcessCritiqueRequest = {
  taskId: string;
  mostRecentPrompt: string;
  groundTruth?: string;
  processSteps: ProcessSupervisionChunk[];
};

export const fetchModelResponse = (
  payload: ProcessCritiqueRequest,
): Promise<ProcessCritiqueResponse> => {
  return apiFetch<ProcessCritiqueResponse>(null, `internal/genai/processCritique`, {
    body: { ...payload, processSteps: JSON.stringify(payload.processSteps) },
    maxRetries: 2,
    method: 'POST',
    parseAndHandle: true,
    redirectOnUnauthorized: true,
  });
};

const useProcessCritique = (critiqueDataFromResponse?: ProcessCritiqueOutput) => {
  const [critiqueData, setCritiqueData] = useState<ProcessCritiqueOutput>(
    critiqueDataFromResponse ?? {
      step: null,
      reason: null,
      correctedStep: null,
      hasRun: false,
      loading: false,
      error: false,
    },
  );

  const getProcessCritique: (params: ProcessCritiqueRequest) => Promise<void> = useCallback(
    async (params: ProcessCritiqueRequest) => {
      setCritiqueData({
        step: null,
        reason: null,
        correctedStep: null,
        loading: true,
        hasRun: false,
        error: false,
      });

      try {
        const request = await fetchModelResponse(params);
        setCritiqueData({ ...request, hasRun: true, loading: false, error: false });
      } catch (error) {
        setCritiqueData({
          step: null,
          reason: null,
          correctedStep: null,
          hasRun: true,
          loading: false,
          error: true,
        });
      }
    },
    [],
  );

  return {
    critiqueData,
    getProcessCritique,
  };
};

export default useProcessCritique;
